import { EventBySlugQuery, GetHeroImageQuery } from "@src/@types/api.d";
import Image from "next/image";
import React from "react";
import { createPortal } from "react-dom";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";

export type HeaderNavItem = {
  id: string;
  label: string;
};

type MonetHeaderProps = {
  slugData?: EventBySlugQuery;
  heroImageData?: GetHeroImageQuery;
  navItems: HeaderNavItem[];
  activeId: string;
  onNavigate: (id: string) => void;
};

const splitNames = (name?: string | null) => {
  if (!name) return ["", ""];

  const ampersandParts = name.split(/\s*&\s*/);
  if (ampersandParts.length >= 2) {
    return [ampersandParts[0], ampersandParts.slice(1).join(" & ")];
  }

  const andParts = name.split(/\s+and\s+/i);
  if (andParts.length >= 2) {
    return [andParts[0], andParts.slice(1).join(" and ")];
  }

  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return [words[0], words.slice(1).join(" ")];
  }

  return [name, ""];
};

const MonetHeader: React.FC<MonetHeaderProps> = ({
  slugData,
  heroImageData,
  navItems,
  activeId,
  onNavigate,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const event = slugData?.eventBySlug;
  const [firstName, secondName] = splitNames(event?.name);
  const heroImage =
    heroImageData?.getHeroImage ||
    event?.hero_image_thumbnail ||
    flowerBackground;

  const navigate = (id: string) => {
    onNavigate(id);
    setIsOpen(false);
  };

  const mobileMenu = isOpen ? (
    <div
      className="fixed inset-x-0 flex flex-col overflow-hidden border-b border-[#228B22]/20 bg-[#1C1820] px-5 py-8 md:hidden"
      style={{
        top: 68,
        bottom: 0,
        height: "calc(100vh - 68px)",
        minHeight: "calc(100dvh - 68px)",
        zIndex: 2147483647,
      }}
    >
      <nav className="flex min-h-0 flex-1 flex-col items-center justify-start overflow-y-auto pt-4">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.id)}
            className={`py-2 text-center font-corGaramond text-[2rem] font-light leading-[1.18] ${
              activeId === item.id ? "text-[#228B22]" : "text-white"
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>
      {event?.hash_tag && (
        <span className="shrink-0 pb-3 text-center font-jost text-[0.52rem] font-normal uppercase tracking-[0.3em] text-white/25">
          {event.hash_tag}
        </span>
      )}
    </div>
  ) : null;

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 flex h-[68px] items-center justify-between border-b border-[#228B22]/20 bg-[#FAF7F5]/95 px-5 backdrop-blur-xl md:px-12"
        style={{ zIndex: 2147483646 }}
      >
        <button
          type="button"
          onClick={() => navigate("hero")}
          className="flex min-w-0 items-center gap-3"
          aria-label="Go to hero"
        >
          <span className="relative h-[34px] w-[34px] shrink-0 overflow-hidden rounded-full border border-[#228B22]">
            <Image
              src={heroImage}
              alt=""
              layout="fill"
              className="object-cover object-center"
            />
          </span>
          <span className="truncate font-corGaramond text-[1.35rem] tracking-[0.06em] text-[#228B22]">
            {firstName?.charAt(0) || "M"} + {secondName?.charAt(0) || "M"}
          </span>
        </button>

        <nav className="hidden items-center justify-end gap-7 md:flex">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.id)}
              className={`relative pb-1 font-jost text-[0.65rem] font-normal uppercase tracking-[0.16em] transition ${
                activeId === item.id
                  ? "text-[#228B22]"
                  : "text-[#401d15]/60 hover:text-[#228B22]"
              }`}
            >
              {item.label}
              <span
                className={`absolute bottom-0 left-0 h-px bg-[#228B22] transition-all ${
                  activeId === item.id ? "w-full" : "w-0"
                }`}
              />
            </button>
          ))}
        </nav>

        <button
          className="flex h-10 w-10 flex-col items-center justify-center gap-[5px] md:hidden"
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          aria-label="Open navigation"
          aria-expanded={isOpen}
        >
          <span
            className={`h-px w-6 bg-[#401d15] transition ${
              isOpen ? "translate-y-[6px] rotate-45" : ""
            }`}
          />
          <span
            className={`h-px w-6 bg-[#401d15] transition ${
              isOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`h-px w-6 bg-[#401d15] transition ${
              isOpen ? "-translate-y-[6px] -rotate-45" : ""
            }`}
          />
        </button>
      </header>

      {mobileMenu && typeof document !== "undefined"
        ? createPortal(mobileMenu, document.body)
        : mobileMenu}
    </>
  );
};

export default MonetHeader;
